import type { Props as QuickshopProps } from '#commerce/components/dialog/quickshop/Quickshop.vue'
import type { Props as VariantSelectionProps } from '#commerce/components/dialog/variant-selection/VariantSelection.vue'
import type { BaseForm as BaseFormType } from '#components'
import type { ContentProductEspots } from '#root/api/clients/content/data-contracts'
import type {
  InventoryData,
  ProductAttributesOptionsColor,
  ProductPromotion,
  ProductReviewsQuestionsResponse,
  ProductReviewsRollupProperty,
  ProductVariants
} from '#root/api/clients/product/data-contracts'
import type { Props as PickupInStoreProps } from '#types/components/product/pickupInStore'
import type { ProductQuestionsParams, ProductReviewsData, ProductReviewsParams } from '#types/components/product/reviews'
import type { InteractionOrigin, ProductContext } from '#types/gtm'
import type { GalleryEspotItem, ImageItem, SketchfabItem, VideoItem } from '#types/media'
import type { BaseNotification } from '#types/notification'
import type {
  AttributeSelection,
  BundleSelectionItem,
  ColorOption,
  GiftCardFormFields,
  PriceFull,
  ProductAttribute,
  ProductAttributeSelectionLabels,
  ProductAttributeType,
  ProductExtended,
  ProductUpsell,
  SelectedVariant
} from '#types/product'
import type { InjectionKey, Reactive } from 'vue'

interface PdpContext {
  addFavorite: () => Promise<void>
  addToCart: (payload?: any) => Promise<boolean>
  addToCartLoading: Ref<boolean>
  attributeSelection: AttributeSelection
  attributeSelectionFormRef: Ref<InstanceType<typeof BaseFormType> | null>
  attributeSelectionLabels: Ref<ProductAttributeSelectionLabels>
  bundleAddToCartLabel: ComputedRef<string | undefined>
  bundleItemCount: ComputedRef<number>
  bundlePromo: ComputedRef<ProductPromotion | undefined>
  bundleSelection: Ref<BundleSelectionItem[]>
  bundleUpsells: ComputedRef<ProductUpsell[]>
  defaultVariant: ComputedRef<SelectedVariant>
  dialogNotification: BaseNotification
  eSpots: ComputedRef<ContentProductEspots['sectionsMap'] | undefined>
  error: Ref<boolean>
  eyebrowStyle: Ref<any>
  findInStore: (attributeSelectionLabels: ProductAttributeSelectionLabels, mode?: PickupInStoreProps['mode']) => Promise<void>
  fitRating: Ref<ProductReviewsRollupProperty | undefined>
  galleryItems: Ref<(ImageItem | VideoItem | SketchfabItem | GalleryEspotItem)[]>
  galleryPending: ComputedRef<boolean>
  getAttrOptionAvailable: (type: ProductAttributeType, value: string) => boolean | undefined
  getAttrOptionNotifyMe: (type: ProductAttributeType, value: string) => boolean | undefined
  getMatchingVariant: (type: ProductAttributeType, value: string) => ProductVariants[number] | undefined
  giftCardForm: Ref<GiftCardFormFields>
  handleAddToCart: () => void
  handleApplePay: () => void
  handleNextAttribute: (options?: Omit<VariantSelectionProps, 'attribute'>, callback?: () => void) => void
  handleNotifyMe: () => void
  handleInStore: (mode: PickupInStoreProps['mode']) => void
  hasFitFinderContent: Ref<boolean>
  inventory: Ref<InventoryData | null>
  inventoryError: Ref<Error | null>
  isComingSoon: Ref<boolean>
  isDigitalProduct: Ref<boolean>
  isFranchisePdp: Ref<boolean>
  isFavorite: Ref<boolean>
  isFavoritePending: Ref<boolean>
  isNonLoyaltyMember: Ref<boolean>
  isNotifyMe: ComputedRef<boolean | undefined>
  isOutOfStock: Ref<boolean>
  isRobisOnly: Ref<boolean | undefined>
  isSelectedVariantOutOfStock: Ref<boolean | undefined>
  isSignInToBuy: Ref<boolean>
  modelMeasurements: Ref<string[] | undefined>
  nextAttribute: Ref<ProductAttribute | undefined>
  onZoom: (index: number, zoomFactor: number) => void
  openQuickshop:
  (
    payload: any,
    options: {
      context?: QuickshopProps['context']
      gtmConfig?: ProductContext & { appSource?: InteractionOrigin }
      initialAttributes?: QuickshopProps['initialAttributes']
      isUpsell?: QuickshopProps['isUpsell']
      variant: QuickshopProps['variant']
    }
  )
  => Promise<any>
  openShopTheLook: (payload: any) => void
  pending: Ref<boolean>
  product: Ref<ProductExtended | null>
  productColor: Ref<ProductAttributesOptionsColor | null>
  productError: Ref<Error | null>
  productId: Ref<string>
  productPending: Ref<boolean>
  productPrice: Ref<PriceFull | undefined>
  previewColor: Ref<ColorOption | null>
  questionsData: Ref<ProductReviewsQuestionsResponse | null>
  questionsError: Ref<Error | null>
  questionsPending: Ref<boolean>
  questionsQuery: ProductQuestionsParams
  removeBundleSelection: (id: string) => void
  removeFavorite: () => Promise<void>
  resetNotification: () => void
  reviewsData: Ref<ProductReviewsData | undefined>
  reviewsError: Ref<Error | null>
  reviewsPending: Ref<boolean>
  reviewsQuery: Reactive<ProductReviewsParams>
  reviewsSummaryData: Ref<ProductReviewsData | undefined>
  selectColor: (_id: any, url: any, colorLabel?: string) => Promise<void>
  selectSize: (type: any, value: any, label?: string) => void
  setColorPreview: (option: ColorOption) => void
  clearColorPreview: () => void
  selectedVariant: Ref<SelectedVariant>
  setNotification: (payload: any) => void
  shippingMethod: Ref<'standard' | 'pickup' | undefined>
  stickyCtaDisplay: Ref<{
    product: ProductExtended | null
    attributeSelection: AttributeSelection
  }>
  showFavorites: Ref<boolean>
  showFindInStore: Ref<boolean>
  showFitFinder: Ref<boolean>
  showFitRating: Ref<boolean>
  showModelMeasurements: Ref<boolean>
  showShopTheLook: Ref<boolean>
  showSizeChart: Ref<boolean>
  triggerApplePaySession: () => void
  upsertBundleSelection: (selection: BundleSelectionItem) => void
  variantRequestPending: ComputedRef<boolean>
}

export const PdpContextKey: InjectionKey<PdpContext> = Symbol('PdpContext')
